// Reusable components
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h2 className="mb-2 text-2xl font-bold text-blue">{title}</h2>
);

const InfoSection: React.FC<{ title: string; content: React.ReactNode }> = ({
  title,
  content,
}) => (
  <div className="mb-8">
    <SectionHeader title={title} />
    {content}
  </div>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="ml-5 flex list-outside list-disc flex-col gap-1">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a href={href} className="break-words underline" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const CookieTable: React.FC = () => (
  <div className="mt-4 grid grid-cols-3 overflow-hidden border border-gray-dark text-sm md:text-base">
    <div className="flex items-center border-b-1 border-r-1 border-gray-dark p-2 font-bold">
      Name
    </div>
    <div className="flex items-center border-b-1 border-r-1 border-gray-dark p-2 font-bold">
      Function
    </div>
    <div className="flex items-center border-b-1 border-gray-dark p-2 font-bold">
      Duration
    </div>
    <div className="hidden items-center border-b-1 border-r-1 border-gray-dark p-2 md:flex">
      _secure_session_id
    </div>
    <div className="flex h-full flex-col items-center justify-center border-b-1 border-r-1 border-gray-dark p-2 md:hidden">
      <div>_secure_</div>
      <div>session_id</div>
    </div>
    <div className="flex items-center border-b-1 border-r-1 border-gray-dark p-2">
      Used in connection with navigation through a storefront
    </div>
    <div className="flex items-center border-b-1 border-gray-dark p-2">
      24h
    </div>
    <div className="flex items-center border-b-1 border-r-1 border-gray-dark p-2">
      keep_alive
    </div>
    <div className="flex items-center border-b-1 border-r-1 border-gray-dark p-2">
      Used in connection with buyer localization
    </div>
    <div className="flex items-center border-b-1 border-gray-dark p-2">
      2w
    </div>
    <div className="hidden items-center border-r-1 border-gray-dark p-2 md:flex">
      secure_customer_sig
    </div>
    <div className="flex h-full flex-col items-center justify-center border-r-1 border-gray-dark p-2 md:hidden">
      <div>secure_cus</div>
      <div>tomer_sig</div>
    </div>
    <div className="flex items-center border-r-1 border-gray-dark p-2">
      Used in connection with customer login
    </div>
    <div className="flex items-center p-2">20y</div>
  </div>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <section className="mb-11 font-poppins text-gray-dark">
      {/* Header Section */}
      <section className="w-full bg-gray-light py-12">
        <div className="flex h-full flex-col items-center justify-center gap-6">
          <h1 className="text-2xl font-bold text-blue md:text-3xl">
            Privacy Policy
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="mx-auto mt-11 max-w-5xl px-10 font-poppins md:px-0">
        {/* Introduction */}
        <p className="mb-8">
          This Privacy Policy describes how pupprofile.org (the "Site" or "we")
          collects, uses, and discloses your Personal Information when you visit
          or make a purchase from the Site.
        </p>

        {/* Contact */}
        <InfoSection
          title="Contact"
          content={
            <p>
              After reviewing this policy, if you have additional questions, want
              more information about our privacy practices, or would like to make
              a complaint, please contact us by e-mail at{" "}
              <span className="underline">pupprofileteam@gmail.com</span>.
            </p>
          }
        />

        {/* Collecting Personal Information */}
        <InfoSection
          title="Collecting Personal Information"
          content={
            <>
              <p className="mb-8">
                When you visit the Site, we collect certain information about your
                device, your interaction with the Site, and information necessary to
                process your purchases. We may also collect additional information
                if you contact us for customer support. In this Privacy Policy, we
                refer to any information about an identifiable individual (including
                the information below) as "Personal Information". See the list below
                for more information about what Personal Information we collect and
                why.
              </p>
              <ul className="list-inside list-disc">
                <div className="mb-8">
                  <li className="mb-2 font-bold">Device information</li>
                  <ul className="ml-9 flex list-outside list-disc flex-col gap-1">
                    <li>
                      <span className="font-bold">Purpose of collection:</span> to
                      load the Site accurately for you, and to perform analytics on
                      Site usage to optimize our Site.
                    </li>
                    <li>
                      <span className="font-bold">Source of collection:</span>{" "}
                      Collected automatically when you access our Site using
                      cookies, log files, web beacons, tags, or pixels
                    </li>
                    <li>
                      <span className="font-bold">
                        Personal Information collected:
                      </span>{" "}
                      version of web browser, IP address, time zone, cookie
                      information, what sites or products you view, search terms,
                      and how you interact with the Site
                    </li>
                  </ul>
                </div>
                <div className="mb-8">
                  <li className="mb-2 font-bold">Order information</li>
                  <ul className="ml-9 flex list-outside list-disc flex-col gap-1">
                    <li>
                      <span className="font-bold">Purpose of collection:</span> to
                      provide products or services to you to fulfill our contract,
                      to process your payment information, arrange for shipping, and
                      provide you with invoices and/or order confirmations,
                      communicate with you, screen our orders for potential risk or
                      fraud, and when in line with the preferences you have shared
                      with us, provide you with information or advertising relating
                      to our products or services.
                    </li>
                    <li>
                      <span className="font-bold">Source of collection:</span>{" "}
                      collected from you.
                    </li>
                    <li>
                      <span className="font-bold">
                        Disclosure for a business purpose:
                      </span>{" "}
                      shared with our processor Stripe
                    </li>
                    <li>
                      <span className="font-bold">
                        Personal Information collected:
                      </span>{" "}
                      name, billing address, shipping address, payment information
                      (including credit card numbers), email address, and phone
                      number.
                    </li>
                  </ul>
                </div>
                <div className="mb-8">
                  <li className="mb-2 font-bold">Customer support information</li>
                  <ul className="ml-9 flex list-outside list-disc flex-col gap-1">
                    <li>
                      <span className="font-bold">Purpose of collection:</span> to
                      provide customer support.
                    </li>
                    <li>
                      <span className="font-bold">Source of collection:</span>{" "}
                      collected from you.
                    </li>
                    <li>
                      <span className="font-bold">
                        Disclosure for a business purpose:
                      </span>{" "}
                      No outside vendors are used
                    </li>
                    <li>
                      <span className="font-bold">
                        Personal Information collected:
                      </span>{" "}
                      None is collected outside of what you provide
                    </li>
                  </ul>
                </div>
              </ul>
            </>
          }
        />

        {/* Minor */}
        <InfoSection
          title="Minor"
          content={
            <p>
              The Site is not intended for individuals under the age of 18. We do
              not intentionally collect Personal Information from children. If you
              are the parent or guardian and believe your child has provided us
              with Personal Information, please contact us at the address above to
              request deletion.
            </p>
          }
        />

        {/* Sharing Personal Information */}
        <InfoSection
          title="Sharing Personal Information"
          content={
            <>
              <p className="mb-2">
                We share your Personal Information with service providers to help us
                provide our services and fulfill our contracts with you, as
                described above. For example:
              </p>
              <ul className="ml-5 flex list-outside list-disc flex-col gap-1">
                <li>
                  We use AWS to power our online store. You can read more about how
                  AWS uses your Personal Information here:{" "}
                  <ExternalLink href="https://aws.amazon.com/privacy/">
                    https://aws.amazon.com/privacy/
                  </ExternalLink>
                </li>
                <li>
                  We may share your Personal Information to comply with applicable
                  laws and regulations, to respond to a subpoena, search warrant or
                  other lawful request for information we receive, or to otherwise
                  protect our rights.
                </li>
              </ul>
            </>
          }
        />

        {/* Behavioral Advertising */}
        <InfoSection
          title="Behavioral Advertising"
          content={
            <>
              <p className="mb-2">
                As described above, we use your Personal Information to provide you
                with targeted advertisements or marketing communications we believe
                may be of interest to you. For example:
              </p>
              <ul className="mb-8 ml-5 flex list-outside list-disc flex-col gap-1">
                <li>
                  We use Google Analytics to help us understand how our customers
                  use the Site. You can read more about how Google uses your
                  Personal Information here:{" "}
                  <ExternalLink href="https://www.google.com/intl/en/policies/privacy/">
                    https://www.google.com/intl/en/policies/privacy/
                  </ExternalLink>
                  . You can also opt-out of Google Analytics here:{" "}
                  <ExternalLink href="https://tools.google.com/dlpage/gaoptout">
                    https://tools.google.com/dlpage/gaoptout
                  </ExternalLink>
                  .
                </li>
              </ul>
              <div className="mb-8">
                For more information about how targeted advertising works, you can
                visit the Network Advertising Initiative's ("NAI") educational page
                at{" "}
                <ExternalLink href="https://www.networkadvertising.org/understanding-online-advertising/how-does-it-work">
                  https://www.networkadvertising.org/understanding-online-advertising/how-does-it-work
                </ExternalLink>
                .
              </div>
              <p className="mb-8">
                We use Google Ads (including the Google Ad Grants program) to display relevant advertisements and reach new audiences. Google may collect or receive certain information from our website and use it to provide measurement services and ad targeting.
              </p>
              <p className="mb-8">
                For more on how Google uses information from sites or apps that use its services, visit:{" "}
                <ExternalLink href="https://policies.google.com/technologies/partner-sites">
                  https://policies.google.com/technologies/partner-sites
                </ExternalLink>
              </p>
              <p className="mb-8">
                We may use remarketing services (such as Google Ads Remarketing) to advertise on third-party websites after you visit our site. These services use cookies to serve ads based on your past visits to our site.
              </p>
              <p className="mb-2">You can opt out of targeted advertising by:</p>
              <ul className="mb-8 ml-5 flex list-outside list-disc flex-col gap-1">
                <li>
                  FACEBOOK -{" "}
                  <ExternalLink href="https://www.facebook.com/settings/?tab=ads">
                    https://www.facebook.com/settings/?tab=ads
                  </ExternalLink>
                </li>
                <li>
                  GOOGLE -{" "}
                  <ExternalLink href="https://www.google.com/settings/ads/anonymous">
                    https://www.google.com/settings/ads/anonymous
                  </ExternalLink>
                </li>
                <li>
                  BING -{" "}
                  <ExternalLink href="https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads">
                    https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads
                  </ExternalLink>
                </li>
              </ul>
              <div>
                Additionally, you can opt out of some of these services by visiting
                the Digital Advertising Alliance's opt-out portal at:{" "}
                <ExternalLink href="https://optout.aboutads.info/">
                  https://optout.aboutads.info/
                </ExternalLink>
                .
              </div>
            </>
          }
        />

        {/* Using Personal Information */}
        <InfoSection
          title="Using Personal Information"
          content={
            <p>
              We use your personal Information to provide our services to you,
              which includes: offering products for sale, processing payments,
              shipping and fulfillment of your order, and keeping you up to date
              on new products, services, and offers.
            </p>
          }
        />

        {/* Lawful basis */}
        <InfoSection
          title="Lawful basis"
          content={
            <>
              <p className="mb-2">
                We use your personal Information to provide our services to you,
                which includes: offering products for sale, processing payments,
                shipping and fulfillment of your order, and keeping you up to date
                on new products, services, and offers.
              </p>
              <BulletList
                items={[
                  "Your consent;",
                  "The performance of the contract between you and the Site;",
                  "Compliance with our legal obligations;",
                  "To protect your vital interests;",
                  "To perform a task carried out in the public interest;",
                  "For our legitimate interests, which do not override your fundamental rights and freedoms.",
                ]}
              />
            </>
          }
        />

        {/* Retention */}
        <InfoSection
          title="Retention"
          content={
            <p>
              When you place an order through the Site, we will retain your
              Personal Information for our records unless and until you ask us to
              erase this information. For more information on your right of
              erasure, please see the 'Your rights' section below.
            </p>
          }
        />

        {/* Automatic decision-making */}
        <InfoSection
          title="Automatic decision-making"
          content={
            <>
              <p className="mb-8">
                If you are a resident of the EEA, you have the right to object to
                processing based solely on automated decision-making (which includes
                profiling), when that decision-making has a legal effect on you or
                otherwise significantly affects you.
              </p>
              <p className="mb-8">
                We DO NOT engage in fully automated decision-making that has a legal
                or otherwise significant effect using customer data.
              </p>
              <p className="mb-8">
                Our processor AWS uses limited automated decision-making to prevent
                fraud that does not have a legal or otherwise significant effect on
                you.
              </p>
              <p className="mb-2">
                Services that include elements of automated decision-making include:
              </p>
              <BulletList
                items={[
                  "Temporary blacklist of IP addresses associated with repeated failed transactions. This blacklist persists for a small number of hours.",
                  "Temporary blacklist of credit cards associated with blacklisted IP addresses. This blacklist persists for a small number of days.",
                ]}
              />
            </>
          }
        />

        {/* Your rights */}
        <InfoSection
          title="Your rights"
          content={
            <>
              <p className="mb-2 font-bold">CCPA</p>
              <p className="mb-8">
                If you are a resident of California, you have the right to access
                the Personal Information we hold about you (also known as the 'Right
                to Know'), to port it to a new service, and to ask that your
                Personal Information be corrected, updated, or erased. If you would
                like to exercise these rights, please contact us through the contact
                information above.
              </p>
              <p className="mb-8">
                If you would like to designate an authorized agent to submit these
                requests on your behalf, please contact us at the address above.
              </p>
            </>
          }
        />

        {/* Cookies */}
        <InfoSection
          title="Cookies"
          content={
            <>
              <p className="mb-8">
                A cookie is a small amount of information that's downloaded to your
                computer or device when you visit our Site. We use a number of
                different cookies, including functional, performance, advertising,
                and social media or content cookies. Cookies make your browsing
                experience better by allowing the website to remember your actions
                and preferences (such as login and region selection). This means you
                don't have to re-enter this information each time you return to the
                site or browse from one page to another. Cookies also provide
                information on how people use the website, for instance whether it's
                their first time visiting or if they are a frequent visitor. We use
                the following cookies to optimize your experience on our Site and to
                provide our services.
              </p>
              <p className="mb-1 font-bold">
                Cookies Necessary for the Functioning of the Store
              </p>
              <CookieTable />
            </>
          }
        />

        {/* Reporting and Analytics */}
        <InfoSection
          title="Reporting and Analytics"
          content={
            <>
              <p className="mb-8">
                The length of time that a cookie remains on your computer or mobile
                device depends on whether it is a "persistent" or "session" cookie.
                Session cookies last until you stop browsing and persistent cookies
                last until they expire or are deleted. Most of the cookies we use
                are persistent and will expire between 30 minutes and two years from
                the date they are downloaded to your device.
              </p>
              <p className="mb-8">
                You can control and manage cookies in various ways. Please keep in
                mind that removing or blocking cookies can negatively impact your
                user experience and parts of our website may no longer be fully
                accessible.
              </p>
              <p className="mb-8">
                Most browsers automatically accept cookies, but you can choose
                whether or not to accept cookies through your browser controls,
                often found in your browser's "Tools" or "Preferences" menu. For
                more information on how to modify your browser settings or how to
                block, manage or filter cookies can be found in your browser's help
                file or through such sites as:{" "}
                <ExternalLink href="https://www.allaboutcookies.org">
                  www.allaboutcookies.org
                </ExternalLink>
                .
              </p>
              <p className="mb-8">
                Additionally, please note that blocking cookies may not completely
                prevent how we share information with third parties such as our
                advertising partners. To exercise your rights or opt-out of certain
                uses of your information by these parties, please follow the
                instructions in the "Behavioral Advertising" section above.
              </p>
            </>
          }
        />

        {/* Do Not Track */}
        <InfoSection
          title="Do Not Track"
          content={
            <p>
              Please note that because there is no consistent industry
              understanding of how to respond to "Do Not Track" signals, we do not
              alter our data collection and usage practices when we detect such a
              signal from your browser.
            </p>
          }
        />

        {/* Changes */}
        <InfoSection
          title="Changes"
          content={
            <p>
              We may update this Privacy Policy from time to time in order to
              reflect, for example, changes to our practices or for other
              operational, legal, or regulatory reasons.
            </p>
          }
        />

        {/* Complaints */}
        <InfoSection
          title="Complaints"
          content={
            <>
              <p className="mb-8">
                As noted above, if you would like to make a complaint, please
                contact us by e-mail or by mail using the details provided under
                "Contact" above.
              </p>
              <p className="mb-8">
                If you are not satisfied with our response to your complaint, you
                have the right to lodge your complaint with the relevant data
                protection authority. You can contact your local data protection
                authority, or our supervisory authority.
              </p>
            </>
          }
        />
      </section>
    </section>
  );
};

export default PrivacyPolicy;
